import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { formatDate } from '@angular/common';

import { JsonHttpService } from '../../shared/http/json-http.service';
import { DataFieldGroup } from '../../shared/datafields/models/datafield-group';
import { TransportInConfiguration, TransportInConfigurationAndValues, TransportOutConfiguration, TransportOutConfigurationAndValues, TransportOutConfigurationSearchResult } from '../models/transport-configuration';
import { TransportType, TransportCategory } from '../models/transport-type';
import { TransportInConfigurationPresentation } from '../models/transport-location-for-presentation';
import { PollingLocationLoggedPoll } from '../models/polling-location-logged-poll';
import { DownloaderService } from 'src/app/shared/download/downloader.service';

@Injectable({
    providedIn: 'root'
})
export class TransportService {
    private http = inject(JsonHttpService);
    private downloaderService = inject(DownloaderService);

    transportTypesOut = [TransportCategory.Send, TransportCategory.Outbox];
    transportTypesIn = [TransportCategory.Polling];

    /* SHARED - BOTH IN & OUT CONFIGURATIONS */

    getTransportTypes(categories: TransportCategory[]): Promise<TransportType[]> {
        return firstValueFrom(this.http.postTyped<TransportType[]>('/api/transport/get-transport-types', categories));
    }

    getTransportDisplayGroups(transportTypeId: number, transportConfigurationId?: number): Promise<DataFieldGroup[]> {
        const options = {
            params: new HttpParams()
                .set('transportTypeId', `${transportTypeId}`)
        };
        if (transportConfigurationId) {
            options.params = options.params.set('transportConfigurationId', `${transportConfigurationId}`);
        }
        return this.http.get('/api/transport/get-transport-display-groups', options).toPromise();
    }

    /* IN-CONFIGURATION SPECIFIC METHODS */

    getTransportInConfigurationForPresentation(): Promise<TransportInConfigurationPresentation[]> {
        return this.http.get('/api/transport/get-transport-in-configurations-presentation').toPromise();
    }

    isTransportInConfigurationNameAvailable(transportConfigurationName: string, ignoreTransportConfigurationId?: number): Promise<boolean> {
        const options = { params: new HttpParams().set('transportConfigurationName', `${transportConfigurationName}`) };

        if (ignoreTransportConfigurationId) {
            options.params = options.params.set('ignoreTransportConfigurationId', `${ignoreTransportConfigurationId}`);
        }
        return this.http.get('/api/transport/is-transport-in-configuration-name-available', options).toPromise();
    }

    async getTransportInConfigurations(transportConfigurationIds: number[]): Promise<TransportInConfiguration[]> {
        const response = await firstValueFrom(this.http.postTyped<TransportInConfiguration[]>('/api/transport/get-transport-in-configurations-by-ids', transportConfigurationIds));
        return response.map(e => new TransportInConfiguration(e));
    }

    saveTransportInConfiguration(transportInConfigurationAndValues: TransportInConfigurationAndValues): PromiseLike<TransportInConfiguration> {
        return this.http.post('/api/transport/save-transport-in-configuration', transportInConfigurationAndValues).toPromise();
    }

    deleteTransportInConfiguration(transportConfigurationId: number): Promise<void> {
        const options = { params: new HttpParams().set('id', `${transportConfigurationId}`) };
        return this.http.post('/api/transport/delete-transport-in-configuration', null, options).toPromise();
    }

    setTriggerNowForInConfiguration(transportConfigurationId: number): Promise<void> {
        const options = { params: new HttpParams().set('id', `${transportConfigurationId}`) };
        return this.http.post('/api/transport/transport-in-configuration-trigger-now', null, options).toPromise();
    }

    setEnableDisableForInConfigurations(transportConfigurationIds: number[], enabled: boolean): Promise<void> {
        const options = { params: new HttpParams().set('enabled', `${enabled}`) };
        return firstValueFrom(this.http.post('/api/transport/transport-in-configurations-enable-disable', transportConfigurationIds, options));
    }

    getLoggedPollsForInConfiguration(transportConfigurationId: number): Observable<PollingLocationLoggedPoll[]> {
        const options = { params: new HttpParams().set('transportConfigurationId', `${transportConfigurationId}`) };
        return this.http.getTyped<PollingLocationLoggedPoll[]>('/api/transport/get-logged-polls', options).pipe(
            map(result => result.map(item => {
                item.StartDate = new Date(item.StartDate);
                item.EndDate = new Date(item.EndDate);
                item.Duration = (item.EndDate.getTime() - item.StartDate.getTime()) / 1000;
                // item.LogLevelText = this.LogLevelTextFromLogLevel(item.LogLevelText);
                return item;
            }))
        );
    }

    getPollLogForInConfiguration(logId: number): Observable<PollingLocationLoggedPoll> {
        const options = { params: new HttpParams().set('logId', `${logId}`) };
        return this.http.getTyped<PollingLocationLoggedPoll>('/api/transport/get-poll-log', options).pipe(
            map(result => {
                result.StartDate = new Date(result.StartDate);
                result.EndDate = new Date(result.EndDate);
                result.Duration = (result.EndDate.getTime() - result.StartDate.getTime()) / 1000;
                // item.LogLevelText = this.LogLevelTextFromLogLevel(item.LogLevelText);
                return result;
            })
        );
    }

    /* OUT-CONFIGURATION SPECIFIC METHODS */

    getTransportOutConfiguration(configId: number): Promise<TransportOutConfiguration> {
        const options = { params: new HttpParams().set('configId', `${configId}`) };
        return this.http.get('/api/transport/get-transport-out-configuration', options).toPromise().catch(() => null);
    }

    getPartnerTransportOutConfigurations(partnerId: number, includeGlobal: boolean): Promise<TransportOutConfiguration[]> {
        const options = { params: new HttpParams() };
        options.params = options.params.set('partnerId', `${partnerId}`);
        options.params = options.params.set('includeGlobal', `${includeGlobal}`);
        return this.http.get('/api/transport/get-partner-transport-out-configurations', options).toPromise();
    }

    getGlobalTransportOutConfigurations(): Promise<TransportOutConfiguration[]> {
        return this.http.get('/api/transport/get-global-transport-out-configurations').toPromise();
    }

    getAllTransportOutConfigurations(): Promise<TransportOutConfigurationSearchResult> {
        return this.http.get('/api/transport/get-all-transport-out-configurations').toPromise();
    }

    deleteTransportOutConfiguration(transportConfigurationId: number): Promise<void> {
        const options = { params: new HttpParams().set('id', `${transportConfigurationId}`) };
        return this.http.post('/api/transport/delete-transport-out-configuration', null, options).toPromise();
    }

    saveTransportOutConfiguration(transportOutConfigurationAndValues: TransportOutConfigurationAndValues): Promise<TransportOutConfiguration> {
        return this.http.post('/api/transport/save-transport-out-configuration', transportOutConfigurationAndValues).toPromise();
    }

    unblockTransportOutConfigurations(transportConfigurationIds: number[]): Promise<TransportOutConfiguration[]> {
        return firstValueFrom(this.http.postTyped<TransportOutConfiguration[]>('/api/transport/unblock-transport-out-configurations', transportConfigurationIds));
    }

    unblockTransportInConfigurations(transportConfigurationIds: number[]): Promise<TransportInConfigurationPresentation[]> {
        return firstValueFrom(this.http.postTyped<TransportInConfigurationPresentation[]>('/api/transport/unblock-transport-in-configurations', transportConfigurationIds));
    }

    isTransportOutConfigurationNameAvailable(partnerId: number, transportConfigurationName: string, ignoreTransportConfigurationId?: number): Promise<boolean> {
        const options = { params: new HttpParams().set('transportConfigurationName', `${transportConfigurationName}`) };

        if (partnerId) {
            options.params = options.params.set('partnerId', `${partnerId}`);
        }
        if (ignoreTransportConfigurationId) {
            options.params = options.params.set('ignoreTransportConfigurationId', `${ignoreTransportConfigurationId}`);
        }
        return this.http.get('/api/transport/is-transport-out-configuration-name-available', options).toPromise();
    }

    async cloneTransportOutConfiguration(transportConfiguration: TransportOutConfiguration): Promise<TransportOutConfiguration> {
        const options = { params: new HttpParams().set('id', `${transportConfiguration.Id}`) };
        return this.http.post('/api/transport/clone-transport-out-configuration', null, options).toPromise();
    }

    async exportTransportOutConfigurations(transportConfigurationIds: number[]): Promise<void> {
        const filename = `OutgoingTransportExport_${formatDate(new Date(), 'yyyyMMdd_HHmmss', 'en')}`;
        const body = { filename, ids: transportConfigurationIds };
        await this.http.post('/api/transport/export-transport-out-configuration', body).pipe(
            switchMap(token => this.downloaderService.downloadFile(token))
        ).toPromise();
    }

    async exportTransportInConfigurations(transportConfigurationIds: number[]): Promise<void> {
        const filename = `IncomingTransportExport_${formatDate(new Date(), 'yyyyMMdd_HHmmss', 'en')}`;
        const body = { filename, ids: transportConfigurationIds };
        await this.http.post('/api/transport/export-transport-in-configuration', body).pipe(
            switchMap(token => this.downloaderService.downloadFile(token))
        ).toPromise();
    }
}
